import React, { useState, useCallback, useEffect } from 'react';
import { parseOpenJobs, parseARaging, computeClientMetrics, computeWeeklyJobStats, computeGlobalFlags } from './parsers';
import { parseJobsHistory, computeWeeklyVolumeStats, computeClientVolumeSparklines, computeHistoryAlerts, computeGhostJobs, computeRevenueGap } from './parsers/jobsHistory';
import { buildUnifiedClients, computeUnifiedFlags } from './parsers/unifiedClient';
import { loadOpsData, saveOpsData, loadJobsHistory, saveJobsHistory, loadClientMasterDB, saveClientMasterDB, emptyEntityData } from './supabase';
import { updateClient } from './data/clientMaster';
import ClientPulse from './views/ClientPulse';
import JobsPipeline from './views/JobsPipeline';
import Collections from './views/Collections';
import FlagsView from './views/FlagsView';
import SaudiOps from './views/SaudiOps';
import FileUpload from './components/FileUpload';
import ClientSetup from './components/ClientSetup';
import Login, { checkAuth } from './components/Login';
import { signOut } from './supabase';
import './App.css';

const TABS = [
  { id: 'flags', label: 'Flags' },
  { id: 'pulse', label: 'Client Pulse' },
  { id: 'pipeline', label: 'Pipeline' },
  { id: 'opshistory', label: 'Ops History' },
  { id: 'collections', label: 'Collections' },
];

export default function App() {
  const [authed, setAuthed] = useState(checkAuth());

  const handleLogout = async () => {
    await signOut();
    setAuthed(false);
  };
  const [tab, setTab] = useState('flags');
  const [showUpload, setShowUpload] = useState(false);
  const [showSetup, setShowSetup] = useState(false);
  const [clientMaster, setClientMaster] = useState({});
  const [data, setData] = useState({ uae: emptyEntityData(), saudi: emptyEntityData() });
  const [jobsHistory, setJobsHistory] = useState({ uae: [], saudi: [] });
  const [loading, setLoading] = useState(true);
  const [saveStatus, setSaveStatus] = useState(null);

  useEffect(() => {
    if (!authed) return;
    setLoading(true);
    Promise.all([
      loadOpsData(),
      loadClientMasterDB(),
      loadJobsHistory('uae'),
    ]).then(([opsData, clientMasterData, uaeHistory]) => {
      setData(opsData);
      setClientMaster(clientMasterData);
      setJobsHistory({ uae: uaeHistory || [], saudi: [] });
      setLoading(false);
    }).catch(err => {
      console.error('Failed to load data:', err);
      setLoading(false);
    });
  }, [authed]);

  const handleFiles = useCallback((entityId, files) => {
    const opsUpdates = {};
    let historyJobs = null;

    const promises = files.map(({ type, buffer }) => {
      return new Promise((resolve) => {
        try {
          if (type === 'jobs') {
            opsUpdates.jobs = parseOpenJobs(buffer);
            opsUpdates.loaded = { ...(opsUpdates.loaded || {}), jobs: true };
          } else if (type === 'jobsHistory') {
            historyJobs = parseJobsHistory(buffer);
          } else if (type === 'ar') {
            opsUpdates.ar = parseARaging(buffer);
            opsUpdates.loaded = { ...(opsUpdates.loaded || {}), ar: true };
          }
        } catch (e) {
          console.error(`Failed to parse ${type}:`, e);
        }
        resolve();
      });
    });

    Promise.all(promises).then(() => {
      if (Object.keys(opsUpdates).length > 0) {
        setData(prev => {
          const next = {
            ...prev,
            uae: {
              ...prev.uae,
              ...opsUpdates,
              loaded: { ...(prev.uae?.loaded || {}), ...opsUpdates.loaded },
            },
          };
          setSaveStatus('saving');
          saveOpsData('uae', next.uae)
            .then(() => { setSaveStatus('saved'); setTimeout(() => setSaveStatus(null), 2000); })
            .catch(() => { setSaveStatus('error'); setTimeout(() => setSaveStatus(null), 4000); });
          return next;
        });
      }

      if (historyJobs) {
        setJobsHistory(prev => {
          const next = { ...prev, uae: historyJobs };
          setSaveStatus('saving');
          saveJobsHistory('uae', historyJobs)
            .then(() => { setSaveStatus('saved'); setTimeout(() => setSaveStatus(null), 2000); })
            .catch(() => { setSaveStatus('error'); setTimeout(() => setSaveStatus(null), 4000); });
          return next;
        });
      }
    });
  }, []);

  const updateClientMaster = useCallback((clientName, updates) => {
    setClientMaster(prev => {
      const updated = updateClient(prev, clientName, updates);
      saveClientMasterDB(updated).catch(e => console.error('Failed to save client master:', e));
      return updated;
    });
  }, []);

  if (!authed) return <Login onAuth={() => setAuthed(true)} />;

  if (loading) {
    return (
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        height: '100vh', background: '#0a1628', flexDirection: 'column', gap: 16
      }}>
        <div style={{ fontSize: 32 }}>⏳</div>
        <div style={{ color: '#0e7c7b', fontSize: 18, fontWeight: 600 }}>Loading dashboard…</div>
        <div style={{ color: '#6b7280', fontSize: 13 }}>Fetching latest data</div>
      </div>
    );
  }

  const currentData = data.uae || emptyEntityData();
  const currentHistoryJobs = jobsHistory.uae || [];

  const clientMetrics = computeClientMetrics(currentData.jobs || [], currentData.ar || [], clientMaster, currentHistoryJobs);
  const weeklyStats = computeWeeklyJobStats(currentData.jobs || []);
  const globalFlags = computeGlobalFlags(currentData.jobs || [], currentData.ar || [], null);
  const unifiedClients = buildUnifiedClients(currentData.jobs || [], currentHistoryJobs, currentData.ar || []);
  const unifiedFlags = computeUnifiedFlags(unifiedClients);

  const unifiedClientNames = new Set(
    unifiedFlags
      .filter(f => f.client)
      .map(f => f.client.toLowerCase().replace(/\s+/g, ' ').trim())
  );

  const filteredClientMetrics = clientMetrics.map(cm => ({
    ...cm,
    flags: (cm.flags || []).filter(f => {
      if (f.type !== 'gone_quiet') return true;
      const norm = (cm.name || '').toLowerCase().replace(/\s+/g, ' ').trim();
      return !unifiedClientNames.has(norm);
    }),
  }));

  const dedupedGlobalFlags = globalFlags;
  const historyWeeklyStats = computeWeeklyVolumeStats(currentHistoryJobs);
  const clientSparklines = computeClientVolumeSparklines(currentHistoryJobs);
  const historyAlerts = computeHistoryAlerts(currentHistoryJobs);
  const ghostJobs = computeGhostJobs(currentHistoryJobs);
  const revenueGap = computeRevenueGap(currentHistoryJobs);

  const currentGlobalRedFlags = dedupedGlobalFlags.filter(f => f.severity === 'red').length;
  const currentUnifiedRedFlags = unifiedFlags.filter(f => f.severity === 'red').length;
  const currentClientRedFlags = filteredClientMetrics.flatMap(c => c.flags).filter(f => f.severity === 'red').length;
  const totalRedFlags = currentGlobalRedFlags + currentUnifiedRedFlags + currentClientRedFlags;

  const isDataEmpty = !currentData.loaded?.jobs && !currentData.loaded?.ar;

  return (
    <div className="app">
      <header className="topbar">
        <div className="topbar-brand">
          <span className="brand-name">TREJHARA</span>
          <span className="brand-sep">|</span>
          <span className="brand-sub">Operations — UAE 🇦🇪</span>
        </div>
        <div className="topbar-actions">
          {saveStatus === 'saving' && <span style={{ color: '#6b7280', fontSize: 12 }}>Saving…</span>}
          {saveStatus === 'saved' && <span style={{ color: '#10b981', fontSize: 12 }}>✓ Saved</span>}
          {saveStatus === 'error' && <span style={{ color: '#ef4444', fontSize: 12 }}>⚠ Save failed</span>}
          {totalRedFlags > 0 && (
            <button className="flag-badge" onClick={() => setTab('flags')}>
              🔴 {totalRedFlags} flags
            </button>
          )}
          <button className="btn-secondary" onClick={() => setShowSetup(true)}>Clients</button>
          <button className="btn-logout" onClick={handleLogout} title="Sign out">⏻</button>
          <button className="btn-primary" onClick={() => setShowUpload(true)}>Upload Data</button>
        </div>
      </header>

      <nav className="tab-nav">
        {TABS.map(t => {
          const flagCount = t.id === 'flags' ? totalRedFlags : 0;
          return (
            <button
              key={t.id}
              className={`tab-btn ${tab === t.id ? 'active' : ''}`}
              onClick={() => setTab(t.id)}
            >
              {t.label}
              {flagCount > 0 && <span className="tab-badge">{flagCount}</span>}
            </button>
          );
        })}
      </nav>

      <div className="entity-strip">
        <span className="entity-label">🇦🇪 UAE</span>
        {currentData.loaded?.jobs && <span className="data-tag">Jobs: {currentData.jobs.length}</span>}
        {currentData.loaded?.ar && <span className="data-tag">AR: {currentData.ar.length} clients</span>}
        {currentHistoryJobs.length > 0 && <span className="data-tag" style={{ background: '#ede9fe', color: '#7c3aed' }}>History: {currentHistoryJobs.length} jobs</span>}
        {!currentData.loaded?.jobs && !currentData.loaded?.ar && tab !== 'opshistory' && (
          <span className="data-tag warn">No data — upload files to begin</span>
        )}
      </div>

      <main className="main-content">
        {tab === 'opshistory' ? (
          <SaudiOps
            historyJobs={currentHistoryJobs}
            weeklyStats={historyWeeklyStats}
            alerts={historyAlerts}
            ghostJobs={ghostJobs}
            revenueGap={revenueGap}
            entity="uae"
          />
        ) : isDataEmpty ? (
          <EmptyState onUpload={() => setShowUpload(true)} />
        ) : (
          <>
            {tab === 'flags' && (
              <FlagsView globalFlags={[...dedupedGlobalFlags, ...unifiedFlags]} clientMetrics={filteredClientMetrics} entity="uae" allEntitiesFlags={[]} />
            )}
            {tab === 'pulse' && (
              <ClientPulse
                clients={clientMetrics}
                weeklyStats={weeklyStats}
                sparklines={clientSparklines}
                entity="uae"
                onUpdateClient={updateClientMaster}
              />
            )}
            {tab === 'pipeline' && (
              <JobsPipeline jobs={currentData.jobs} weeklyStats={weeklyStats} entity="uae" />
            )}
            {tab === 'collections' && (
              <Collections ar={currentData.ar} jobs={currentData.jobs} clientMaster={clientMaster} entity="uae" />
            )}
          </>
        )}
      </main>

      {showUpload && (
        <FileUpload
          entity="uae"
          onFiles={handleFiles}
          onClose={() => setShowUpload(false)}
        />
      )}
      {showSetup && (
        <ClientSetup
          clients={clientMetrics}
          clientMaster={clientMaster}
          onUpdate={updateClientMaster}
          onClose={() => setShowSetup(false)}
        />
      )}
    </div>
  );
}

function EmptyState({ onUpload }) {
  return (
    <div className="empty-state">
      <div className="empty-icon">📁</div>
      <h2>No data loaded</h2>
      <p>Upload your SCM Profit and Tally exports to get started.</p>
      <p className="empty-files">
        Expected files: <code>OPEN_JOBS.xlsx</code> · <code>JOB_HISTORY.xlsx</code> · <code>AR_AGING.xlsx</code>
      </p>
      <button className="btn-primary large" onClick={onUpload}>Upload Files</button>
    </div>
  );
}
